import { useCallback, useEffect, useState } from 'react';

import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { getDeviceInfo } from '../utils/deviceSettings';
import { getPushPermissionState } from '../utils/nativePush';
import { subscribeNativePushChanged } from '../utils/nativePushChanges';
import {
  detectNativePushState,
  type NativePushPermission,
  type NativePushState,
} from '../utils/nativePushState';

type DeviceState = {
  permission: NativePushPermission;
  /** undefined when the native plugin didn't report it (older shell build). */
  notificationsEnabled?: boolean;
};

/**
 * Can push actually reach this device? Native shell only — always 'unknown' on web, where
 * BrowserPushStatus answers the same question through the web Notification API.
 *
 * Cheap enough to call from more than one place on a screen: it reads the OS permission once
 * per mount and re-reads it on resume, because both the permission and the token can change on
 * screens outside the app (system settings, another account signing in).
 */
export const useNativePushStatus = (): { state: NativePushState; refresh: () => void } => {
  const { profile } = useProfile();
  const [device, setDevice] = useState<DeviceState | undefined>(undefined);

  const refresh = useCallback(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }
    void (async () => {
      // Both readings in one state update, so the banner never flashes the wrong reason
      // on its way to the right one.
      const [permission, info] = await Promise.all([getPushPermissionState(), getDeviceInfo()]);
      setDevice({ permission, notificationsEnabled: info?.notificationsEnabled });
    })();
  }, []);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }
    refresh();
    const unsubscribe = subscribeNativePushChanged(refresh);

    // The permission is granted on a system screen, so the app only learns about it when it
    // comes back to the foreground — same reason PushOnboarding listens for resume.
    let remove: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('resume', () => refresh());
        if (cancelled) {
          void handle.remove();
        } else {
          remove = () => void handle.remove();
        }
      } catch {
        // @capacitor/app unavailable — the mount-time read still stands
      }
    })();

    return () => {
      unsubscribe();
      cancelled = true;
      remove?.();
    };
  }, [refresh]);

  if (!envConfig.IS_NATIVE) {
    return { state: 'unknown', refresh };
  }

  // me.pushToken is `[{id, token}]` — compare the token strings with what this device stored.
  // A null list means "not known yet": the profile restored from the app's snapshot carries no
  // tokens (features/snapshot), and until the live profile arrives the state must stay unknown
  // instead of claiming this device is not registered.
  const serverTokens = profile?.pushToken
    ? profile.pushToken
        .map((entry) => entry?.token)
        .filter((token): token is string => typeof token === 'string' && token.length > 0)
    : null;

  return {
    state: detectNativePushState({
      permission: device?.permission,
      systemNotificationsEnabled: device?.notificationsEnabled,
      localToken: localStorage.getItem('fcm_token'),
      serverTokens,
    }),
    refresh,
  };
};
